import { useEffect, useRef, useCallback, type ReactNode } from 'react';
import { IconButton } from './ui/IconButton';
import './Popup.css';

interface PopupProps {
  children: ReactNode;
  onDismiss?: () => void;
  maxWidth?: number;
  'aria-label'?: string;
}

interface PopupHeaderProps {
  title: ReactNode;
  /** Renders an ✕ close button on the right. */
  onClose?: () => void;
  /** Extra action (e.g. a secondary button) placed before the close. */
  action?: ReactNode;
  className?: string;
}

/**
 * Title bar for a Popup. The title uses the game's stroked hero style.
 * The close ✕ sits in the top-right corner; the optional action slot
 * sits in the top-left (used e.g. for a secondary "Open full tutorial" link).
 */
export function PopupHeader({ title, onClose, action, className }: PopupHeaderProps) {
  return (
    <div className={`popup-header ${className ?? ''}`.trim()}>
      <div className="popup-header__action">{action}</div>
      <h2 className="popup-header__title">{title}</h2>
      <div className="popup-header__close">
        {onClose && (
          <IconButton
            variant="ghost"
            shape="circle"
            size="md"
            onClick={onClose}
            aria-label="Close"
          >
            {'\u2715'}
          </IconButton>
        )}
      </div>
    </div>
  );
}

interface PopupBodyProps {
  children: ReactNode;
  /** Makes the body scroll independently so the header/footer stay pinned. */
  scrollable?: boolean;
  className?: string;
}

/** Content area of a popup. Set `scrollable` for long sheets. */
export function PopupBody({ children, scrollable, className }: PopupBodyProps) {
  return (
    <div
      className={`popup-body${scrollable ? ' popup-body--scrollable' : ''} ${className ?? ''}`.trim()}
    >
      {children}
    </div>
  );
}

interface PopupFooterProps {
  children: ReactNode;
  className?: string;
}

/** Button row at the bottom of a popup. */
export function PopupFooter({ children, className }: PopupFooterProps) {
  return <div className={`popup-footer ${className ?? ''}`.trim()}>{children}</div>;
}

/** Base modal wrapper. Compose with Popup.Header / Popup.Body / Popup.Footer. */
function PopupBase({ children, onDismiss, maxWidth = 420, 'aria-label': ariaLabel }: PopupProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  // Escape key dismissal
  useEffect(() => {
    if (!onDismiss) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onDismiss]);

  // Focus trap
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const card = cardRef.current;
    if (!card) return;
    const focusable = card.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  // Auto-focus first focusable element on mount
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const focusable = card.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    focusable?.focus();
  }, []);

  return (
    <div className="popup-overlay" onClick={onDismiss} role="presentation">
      <div
        ref={cardRef}
        className="popup-card"
        style={{ maxWidth }}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    </div>
  );
}

export const Popup = Object.assign(PopupBase, {
  Header: PopupHeader,
  Body: PopupBody,
  Footer: PopupFooter,
});

